import { shibarium } from 'viem/chains'
import type { ChainType } from '@/constants/chain-specific'
import { SOCIALS } from '../project-specific/socials';
import { defaultIpfsGateway } from '@/config/index';

// Define types inline to avoid import issues
type NFTMetadata = {
  name?: string;
  description?: string;
  image?: string;
  attributes?: Array<{ trait_type: string; value: string | number }>;
  [key: string]: any;
};

type RarityInfo = {
  rank?: number;
  score?: number;
  traitScores?: Record<string, number>;
  [key: string]: any;
};

export interface RoyaltyBreakdownType {
  [key: string]: number
}

export interface NFTCollection {
  // Basic collection information
  name: string
  address: string
  tokenAddress?: string
  tradingEnabled?: boolean

  royalty?: {
    ownerAddress?: string
    ownerContact?: string
    royaltyAddress?: string
    percentTotal?: number
    breakdown?: Array<{ address: string; percent: number; description: string }>
  }
  slug: string // New field for URL-friendly collection identifier
  type?: 'ERC721' | 'ERC1155' | 'DN404'
  description?: string

  chainId: number
  cdnBaseUrl?: string
  symbol?: string
  image?: string

  // Layout and display options
  isTradingCard?: boolean // For collections with tall aspect ratio images that need special layout

  // Social and external links
  website?: string
  twitter?: string
  discord?: string
  opensea?: string

  // Custom metadata and rarity functions
  getTokenMetadata?: (
    tokenId: string | number,
    chainId: number
  ) => Promise<NFTMetadata>
  getTokenImageUrl?: (
    tokenId: string | number,
    metadata?: NFTMetadata
  ) => string
  getRarityInfo?: (
    tokenId: string | number,
    metadata?: NFTMetadata
  ) => Promise<RarityInfo>
  socials?: string[]
  banner?: string
  thumbnail?: string
  collectionType?: 'ERC721' | 'ERC1155' | 'DN404'
  maxSupply?: number
  mintActive?: boolean
  mintLink?: string
  firstId?: number
  formatTokenId?: (tokenId: string | number) => string
  metadataSource?: string
  metadataExt?: string | boolean

  // Non-enumerable collection handling
  noEnumerable?: boolean // True for collections that don't implement ERC721Enumerable

  isGif?: boolean
}

// Collection of supported NFT collections by chain
const COLLECTIONS_BY_CHAIN: Record<number, NFTCollection[]> = {
  // Shibarium collections
  [shibarium.id]: [
    {
      name: 'Octopussy Gang',
      slug: 'octopussy-gang',
      symbol: 'OPGGenz',
      address: '0x07b41b1a6707893ec8abcf7ab9702bcc4539c457',
      description: 'Welcome to the Generative Octopussy Gang Collection! Hand Drawn with Graphic Design Fusion elements! These cute lil NFTs live on the Shibarium Blockchain and are here to help save our Oceans, Freshwater & Marine life! $15 of every original Mint/ Sale goes directly to the charity wallet for donating to Swim, Drink, Fish, Mote Marine Lab & Research center and The Ocean Cleanup project.',
      chainId: shibarium.id,
      banner: 'https://api.chewy.build/assets/cfbf6a2e-1963-4dd7-9148-4d89dc65e46b',
      thumbnail: 'https://api.chewy.build/assets/34cf6550-24e7-44e4-ae3b-83d37b89951c',
      website: 'https://t.me/OPGNFTs',
      socials: ['https://x.com/OPGNFTs', 'https://x.com/OctopussyGang'],
      maxSupply: 4233,
      isTradingCard: true,
      royalty: {
        ownerAddress: '0x51479269cF033fe2e38dD0a41677f20E7E11d689',
        ownerContact: 'https://t.me/CryptoNicOg',
        royaltyAddress: '0x51479269cF033fe2e38dD0a41677f20E7E11d689',
        percentTotal: 1000,
      },
      mintLink: 'https://shibcoop.io/launchpad/opg',
      metadataSource: `${defaultIpfsGateway}Qmb5LDLUMFWDQnXv23iNLcWicLw7y7uLyHCDfXCeqrcUyz/`,
    },

    {
      name: 'Colonel Coup',
      slug: 'colonel-coup',
      symbol: 'COOP',
      address: '0x5e732d23de0a8983601a51c377524cf5165c0a90',
      chainId: shibarium.id,
      description: 'Just a chiken. Official mascot of ShibCoop',
      website: SOCIALS.coop.website,
      socials: ['https://x.com/ColonelCoup1'],
      banner: 'https://api.chewy.build/assets/777cbdde-7a36-40e7-89cb-4ec0f70c6bfe',
      thumbnail: 'https://api.chewy.build/assets/7e794679-8dcd-42f6-bd59-58accd16ca90',
      maxSupply: 194,
      mintActive: false,
    },
    {
      name: 'Dork Wadz Holidaze',
      slug: 'dork-wadz-holidaze',
      symbol: 'HDAZE',
      address: '0x204db8445d98dc92f5f855e71ee7e7c661dea286',
      chainId: shibarium.id,
      description: 'Dork Wadz Holidaze is a collection of 10,000 NFTs that celebrates the holiday season with a mix of festive and quirky designs.',
      website: SOCIALS.bonewhale.website,
      socials: SOCIALS.bonewhale.other,
      banner: 'https://api.chewy.build/assets/b4764e53-dd6b-47de-b7a4-f118f3673834',
      thumbnail: 'https://api.chewy.build/assets/0934c151-6c56-4650-a340-2e30ce937788',
      maxSupply: 6900,
      mintActive: false,
      royalty: {
        ownerAddress: '0x9EdD11D740C5EFa86bcB8e74fce474d975D93193',
        ownerContact: 'https://t.me/smashSketchum',
        royaltyAddress: '0x9EdD11D740C5EFa86bcB8e74fce474d975D93193',
        percentTotal: 500,
      },
    },
    {
      name: 'Shiba Rocks',
      slug: 'shiba-rocks',
      symbol: 'ShibaRocks',
      address: '0x2D195DdAa3444A0b64842CCfFF6a78E13C1A0d25',
      chainId: shibarium.id,
      description: 'Shiba Rocks is a limited NFT collection comprising only 100 Rocks, serving as a historical representation of Shiba Inu Coin.',
      website: 'https://shibarocks.com/',
      socials: ['https://x.com/ShibaRocks', 'https://t.me/shibariumrocks'],
      banner: 'https://api.chewy.build/assets/e67f6066-2fae-475b-9d4a-e9bdf3621b1d',
      thumbnail: 'https://api.chewy.build/assets/7c1a7142-aaaa-4800-a5a6-73d4e7a39087',
      maxSupply: 100,
      isTradingCard: true,
      noEnumerable: true,
    },
    {
      name: 'Shiba Inu Yacht Club',
      slug: 'siyc',
      symbol: 'SIYC',
      address: '0xB33012feB12D590f4ee9B0D9999e95C30303A209',
      description: `SIYC is another version inspired by Shiboshis. It is powered by Woofswap and integrated with NONE.`,
      chainId: shibarium.id,
      website: 'https://id.presalekiller.com/',
      socials: ['https://x.com/woofswap', 'https://t.me/ShibariumSRC20'],
      banner: 'https://api.chewy.build/assets/d5d2c7e2-6fcd-4f6e-b7fa-995efaf88b45',
      thumbnail: 'https://api.chewy.build/assets/e7e1adbe-a9a6-4908-96cd-efaf8bd52ce4',
      maxSupply: 10000,
      tokenAddress: '0xe9Cb2D7ADC24Fc59FE00D6C0A0669BDF16805Fe0',
      type: 'ERC721',
      firstId: 0,
      mintLink: 'https://id.presalekiller.com/'
    },
    {
      name: 'Shiba Sequoia Forest Lads',
      slug: 'shiba-sequoia-forest-lads',
      symbol: 'LADS',
      address: '0xd231f88ebE2081879AF9D3f6a61a50171571A1Bd',
      chainId: shibarium.id,
      description: 'Shiba Sequoia Forest Lads is a collection of 10,000 NFTs that celebrates the beauty of the Sequoia Forest.',
      website: 'https://shibasequoiaforestlads.com/',
      socials: ['https://x.com/ShibaSequoiaLads'],
      banner: 'https://api.chewy.build/assets/8f76e8d8-0ace-4f62-a596-237be7ae1052',
      thumbnail: 'https://api.chewy.build/assets/211876dd-90e5-4622-af80-c313b204afc7',
      maxSupply: 10000,
      mintActive: false,
      mintLink: 'https://shibcoop.io/launchpad/lads',
    },
    {
      name: 'Boarding Apes Yard Club',
      slug: 'boarding-apes-yard-club',
      symbol: 'BAYC',
      address: '0x8D4a10A1a11E461A7a12B163A63649934098E10e',
      chainId: shibarium.id,
      description: `Unlock the ultimate perks with Bling's Boarding Apes Yard Club NFTs: Dive into the Metaverse, flip yourself into socializing events, shred real-life sales commissions from our online store, stake for even more rewards, and score exclusive admissions, rental opportunities, and profit shares in our upcoming skatepark experience! Immerse yourself in this captivating world of digital art with our limited collection of 599, each showcasing unique traits, rarities and meticulously crafted artwork!`,
      banner: 'https://api.chewy.build/assets/df429d97-b647-4fc2-9494-880388d5b3e1',
      thumbnail: 'https://api.chewy.build/assets/b18e02dd-5b30-48b2-926a-75bcc7c03953',
      maxSupply: 599,
      mintActive: false,
      mintLink: 'https://shibcoop.io/launchpad/boarding-apes',
      metadataSource: `${defaultIpfsGateway}QmdzLWhQ7pGCMgAxSiSjFJV1yHi4PQ5QFttYykWZ6H2Ypi/`,
      metadataExt: false
    },
    {
      name: 'Under the Cherry Blossoms',
      slug: 'under-the-cherry-blossoms',
      symbol: 'Cherry Blossoms',
      address: '0xAF2eA9f9185503e5ACDEBb595a76B636Cea2053A',
      description: 'The Under the Cherry Blossoms NFT Collection brings to life an epic 26-chapter tale of loyalty, resilience, and unity, set in the enchanting world of Shibarium. This captivating series follows the journey of Sakura, a determined geisha, and her devoted Shiba Inu, Taro, as they face trials, uncover ancient secrets, and fight to protect their sacred heritage.',
      chainId: shibarium.id,
      website: 'https://geishas.app',
      socials: ['https://t.me/Geishasofshibarium', 'https://x.com/ShibaGeishas'],
      maxSupply: 6500,
      mintActive: false,
      banner: 'https://api.chewy.build/assets/5ac687a7-f4ea-4e13-8238-cdae08885498',
      metadataSource: `${defaultIpfsGateway}bafybeifh7otll4sf6sjcst5bzzwjy7al6zhngsr775ipgrjdeb2phzio5q/`,
      metadataExt: '.json',
      mintLink: 'https://www.geishas.app/mint',
    },
    {
      name: 'The Geishas of Shibarium',
      slug: 'the-geishas-of-shibarium',
      symbol: 'GEISHAS',
      address: '0x9c0e6bb862a75ad53baaf19d52eaa2c0e6592c3d',
      chainId: shibarium.id,
      description: 'A collection of 6,400 Unique and Original NFTs individually created by Old Horse specifically for Shibarium',
      website: 'https://geishasofshibarium.art/',
      socials: ['https://t.me/Geishasofshibarium', 'https://x.com/ShibaGeishas'],
      banner: 'https://api.chewy.build/assets/7a4bc486-ca06-4267-b05b-0d7be7c8fc46',
      thumbnail: 'https://api.chewy.build/assets/1a2b3a53-1c1e-4daa-a68d-3ab7c83c06d4',
      maxSupply: 6400,
      mintActive: false,
    },
    {
      name: 'ShibArcade',
      slug: 'shibarcade',
      symbol: 'ShibArcade',
      address: '0x645a297c8df16e4bc4e492c4f127b2c2724ea440',
      chainId: shibarium.id,
      description: 'ShibArcade: An electrifying NFT collection boasting a retro arcade style, uniting Shibarium communities for social gaming experiences in the digital realm.',
      website: SOCIALS.dcent.website,
      socials: SOCIALS.dcent.other,
      banner: 'https://api.chewy.build/assets/2a356ea5-a0f3-4731-9c72-9b0983c2de5d',
      thumbnail: 'https://api.chewy.build/assets/30461145-22ee-47d3-a4d8-bff911089e81',
      maxSupply: 2222,
      royalty: {
        ownerAddress: '0xCee007327bdec3708bb4b0d221d0E9B9Dd1EBfa3',
        ownerContact: 'https://t.me/ShibArcade',
        royaltyAddress: '0xCee007327bdec3708bb4b0d221d0E9B9Dd1EBfa3',
        percentTotal: 500,
        breakdown: [
          { address: '0xCee007327bdec3708bb4b0d221d0E9B9Dd1EBfa3', percent: 500, description: 'DND721 Development' }
        ]
      },
    },
    {
      name: "Brickheads",
      slug: "brickheads",
      symbol: "Brickheads",
      address: "0x4dcd2c7198bfd5D4eCB774b5edB92FC578C97837",
      chainId: shibarium.id,
      description: "BrickHeads; a unique pixel art collection of 1,111 NFTs dedicated exclusively to the Shibarium Network, built for you “Brick by Brick.",
      website: "https://shibcoop.io/",
      socials: ["https://t.me/ShibCoOp", "https://x.com/ShibCoOp"],
      banner: 'https://api.chewy.build/assets/456bfbbe-7558-4379-a5e3-334f5fb59dbd',
      thumbnail: 'https://api.chewy.build/assets/cab699bd-bd7c-418b-87dc-fc8e52d76d31',
      maxSupply: 1111,
      mintActive: false,
    },
    {
      name: 'Founders',
      slug: 'founders',
      symbol: 'Founders',
      address: '0x3ab2eeca522f83b02a23a24ad9ab81d65363d5d0',
      chainId: shibarium.id,
      description: 'A collection of 10,000 masterfully crafted PFP Avatars, fashionably styled and ready for a metaverse takeover. Founders by ShibCoOp is your gateway into the digital space.',
      website: 'https://shibcoop.io/',
      socials: ['https://t.me/ShibCoOp', 'https://x.com/ShibCoOp'],
      banner: 'https://api.chewy.build/assets/ee17458f-46e6-4feb-aa6f-0c83d8f82da4',
      isTradingCard: true,
      maxSupply: 10000,
    },
    {
      name: 'Shib Dream',
      slug: 'shib-dream',
      symbol: 'DREAM',
      address: '0x4e24db7019ea9d0df8b72b10da149eebaa7d0e78',
      chainId: shibarium.id,
      maxSupply: 3333,
      description: `Shib Dream NFT: A unique collection celebrating the $SHIB community's journey. Limited edition digital art for true enthusiasts, driving the future of NFTs`,
      website: SOCIALS.ltd.website,
      socials: SOCIALS.ltd.other,
      banner: 'https://api.chewy.build/assets/25087a60-2161-4ecb-8c92-1af95fcefcd5',
      thumbnail: 'https://api.chewy.build/assets/1da42e58-438e-47fc-a342-6913cc500795',
      tokenAddress: '0x2aCe8fab52842BEDa0713282d96c683FBE00FF89',
      metadataSource: `${defaultIpfsGateway}QmT1JSH4CCiJB1adFosgVdpFgW3YBE13vu6DayBfYT2583`,
      metadataExt: false,
      royalty: {
        ownerAddress: '0x76ad2c21DD6fBee4Ae7AF8Ad6685D269D04C0a22',
        ownerContact: 'https://t.me/BillKenn',
        royaltyAddress: '0x76ad2c21DD6fBee4Ae7AF8Ad6685D269D04C0a22',
        percentTotal: 500,
        breakdown: [
          { address: '0x76ad2c21DD6fBee4Ae7AF8Ad6685D269D04C0a22', percent: 500, description: 'LTD Buybacks' }
        ]
      },
    },
    {
      name: 'Sideshow 404',
      slug: 'sideshow-404',
      symbol: 'SKULLZ',
      address: '0xf27311e5ff713eda70246f908cda7abe8933dcb2',
      tokenAddress: '0x5212B42ef96A47Af93F3a6c801227b650EDEb12f',
      chainId: shibarium.id,
      description: 'A curated set of 4,040 DN-404 NFT pixel art skulls from Bad Flea Circus, each brimming with carnival charm straight outta the sideshow! Dare to explore the eccentric world of Sideshow 404, where every skull tells a tale of mystery and mischief!',
      website: 'https://radballs.online/',
      socials: ['https://t.me/radballscircus', 'https://x.com/RadballsCircus'],
      type: 'DN404',
      banner: 'https://api.chewy.build/assets/df8ce906-be5b-4c91-b611-8e99dfe6ef7d',
      thumbnail: 'https://api.chewy.build/assets/644966f0-27a0-464e-91dd-60dd50200513',
      maxSupply: 4040,
      royalty: {
        ownerAddress: '0x10df6CF1A8E99629729179a6d838fD2a8D63D7ed',
        ownerContact: 'https://t.me/MrRadballs',
        royaltyAddress: '0x10df6CF1A8E99629729179a6d838fD2a8D63D7ed',
        percentTotal: 500,
      },
    },
    {
      name: 'Lil Dream',
      slug: 'lil-dream',
      symbol: 'LILDREAM',
      address: '0xeBE31126bF3D6770Da9bCEAa8f9A32dA8cD4ecE9',
      chainId: shibarium.id,
      description: `The Lil Dream NFT Collection features hand-drawn, adorable offspring of the Shib Dream and Sharbae collections. Each piece uniquely combines Shib Dream's whimsy with Sharbae's elegance, creating charming characters with vibrant colors and distinctive patterns. This collection is perfect for those who appreciate the fusion of art and digital heritage in the NFT world.`,
      banner: 'https://api.chewy.build/assets/5d476eec-6685-4be1-8fdd-dd0f1630b4d5',
      website: SOCIALS.ltd.website,
      socials: SOCIALS.ltd.other,
      maxSupply: 3333,
      royalty: {
        ownerAddress: '0x76ad2c21DD6fBee4Ae7AF8Ad6685D269D04C0a22',
        ownerContact: 'https://t.me/BillKenn',
        royaltyAddress: '0x76ad2c21DD6fBee4Ae7AF8Ad6685D269D04C0a22',
        percentTotal: 500,
        breakdown: [
          { address: '0x76ad2c21DD6fBee4Ae7AF8Ad6685D269D04C0a22', percent: 500, description: 'LTD Buybacks' }
        ]
      },
    },
    {
      name: 'Tutti Frutti Women',
      slug: 'tutti-frutti-women',
      symbol: 'TFW',
      address: '0xCd0d5af86FCeAe95feedB2e53E6182acbD063c3f',
      chainId: shibarium.id,
      description: 'Mint with Purpose at Tutti Frutti Women—where each NFT is a burst of bravery, beauty, and hope! Join us in breaking cancer stigma, supporting warriors, and spreading awareness. Every Tutti Frutti Woman minted adds a touch of strength and impact.',
      website: 'https://www.tuttifruttiwomen.art/',
      socials: ['https://linktr.ee/tuttifruttiwomen'],
      banner:
        'https://api.chewy.build/assets/f16392db-db9f-4f8a-8b9d-beaf6806b911',
      thumbnail: 'https://api.chewy.build/assets/30133740-831b-467d-8b61-51f7e4a9374c',
      maxSupply: 5000,
      metadataSource: `${defaultIpfsGateway}bafybeihwief5t5kactlasdptszwhjaxdu2ok3kzmtmsr2b7fuqsmvvuyti/`,
      royalty: {
        ownerAddress: '0x336489Fc8113C97F9497769DB3a185A711f44E9c',
        ownerContact: 'https://t.me/tfw_andreia',
        royaltyAddress: '0x336489Fc8113C97F9497769DB3a185A711f44E9c',
        percentTotal: 500,
      },
      mintLink: 'https://shibcoop.io/launchpad/tfw',
    },
    {
      name: 'LumiBots Droid Army',
      slug: 'lumibots',
      symbol: 'LUMI',
      address: '0x57D1E81d16b5771853540Fb8E4aBf1715Ba98643',
      chainId: shibarium.id,
      description: `The LumiBots OG NFT collection of 1,193 total pieces encapsulates the essence of innovation, futurism, and our collective human fascination with technology. Each artwork within this immersive collection exists as a digital masterpiece, meticulously curated with stunning visual details that teleport you to a realm where imagination knows no bounds. LumiBots "Illuminate the future".`,
      website: 'https://lumibots.art',
      socials: ['https://t.me/LumiBotsShibarium/1', 'https://x.com/lumibots_art'],
      banner: 'https://api.chewy.build/assets/becb8f6d-e08a-4c6f-8388-07921ac6bfdf',
      thumbnail: 'https://api.chewy.build/assets/9ac7a887-3913-4b25-ac54-5cca16a571bc',
      maxSupply: 1193,
      royalty: {
        ownerAddress: '0x1f7118005BBF7D28601bc6c6252c35bcd07e3efd',
        ownerContact: 'https://t.me/CliffordRoboshi',
        royaltyAddress: '0x1f7118005BBF7D28601bc6c6252c35bcd07e3efd',
        percentTotal: 500,
      },
      mintLink: 'https://shibcoop.io/launchpad/lumibotsv3',
      metadataSource: `${defaultIpfsGateway}QmeikMHJbjTeoXg7hTJGCwXX5t3e2RET8DyZ8PNChz3av9/`,
      metadataExt: false
    },
    {
      name: 'Shiba Eternity Card Collection',
      slug: 'shiba-eternity-cards',
      symbol: 'Shiba Eternity',
      address: '0x960a6BE0864d154ED63432C3F081Bc3642efba8A',
      type: 'ERC1155',
      description: 'Trade and collect Shiba Eternity game cards',
      isTradingCard: true,
      chainId: shibarium.id,
      socials: ['https://t.me/ShibariumTechnologies'],
      website: 'https://www.shibaeternity.com/',
      banner:
        'https://api.chewy.build/assets/450a25e1-6327-4d8b-89e6-52f0b250902e',
      maxSupply: 100000,
      thumbnail:
        'https://api.chewy.build/assets/09084c17-54cc-4a7c-949a-678540e17482',
      metadataSource: `${defaultIpfsGateway}QmafBgihr8C19oJs9htsYBgP4gpJcpkK4oSM6DikN23UCv/`,
      royalty: {
        ownerAddress: '0x28521a4515297A968cE27788Bf12C1E4043513a5',
        ownerContact: 'https://t.me/shiboshisworld',
        royaltyAddress: '0x28521a4515297A968cE27788Bf12C1E4043513a5',
        percentTotal: 500,
        breakdown: [
          {
            address: '0x28521a4515297A968cE27788Bf12C1E4043513a5',
            percent: 500,
            description: 'SHIB Burn'
          }
        ]
      },
    },
    {
      name: 'Magical Mermaid',
      slug: 'magical-mermaid',
      symbol: 'MM',
      address: '0xCD5d5D58f9CF07A111Eb83e969dd98cfc1baf2F1',
      chainId: shibarium.id,
      description:
        'The Magical Mermaid is a unique collection of magic that exists within the universe, embodying love, happiness, and joy. This collection features 1,000 hand-drawn pieces, with 10% of the proceeds donated to support cancer warriors.',
      maxSupply: 1000,
      website: 'https://linktr.ee/MAGICALMERMAIDS',
      socials: ['https://t.me/MagicalMermaid', 'https://x.com/MagicalMermaid'],
      banner:
        'https://api.chewy.build/assets/90fbeec9-2346-4efe-9d2c-2ea39355c471',
      thumbnail:
        'https://api.chewy.build/assets/0ac3acfc-ed37-419f-9652-e1316e5bca04',
      metadataSource:
        'ipfs://bafybeialxmquqs3sq47sciopkixhvbpn2xngqgnzybajmercuprbrxju6a/',
      royalty: {
        ownerAddress: '0x6F2d928eCf903c0Ca5B2De44C21Ecbef70371B7A',
        ownerContact: 'https://t.me/MAGICALMERMAIDS1',
        royaltyAddress: '0x6F2d928eCf903c0Ca5B2De44C21Ecbef70371B7A',
        percentTotal: 500,
      },
    },
    {
      name: 'Shibarium pups',
      slug: 'shibarium-pups',
      symbol: 'Shups',
      address: '0x3D789D7a5049982Ea174362D718B999bf921Dbe0',
      description:
        'A collection of 6000 of the best bois and their pals on Shibarium. These pups enable you to win rewards and promotions and is the counterpart to the $SHUPS token.',
      chainId: shibarium.id,
      socials: ['https://t.me/Shibariumpups', 'https://x.com/ShibariumPups'],
      website: 'https://www.shibariumpups.com/nft-ecosystem',
      banner:
        'https://api.chewy.build/assets/25fc9e57-d21f-4d15-81e0-10c60c0d6865',
      thumbnail:
        'https://api.chewy.build/assets/3aedab7f-31e1-44da-a7cb-984194c11548',
      metadataSource: 'ipfs://QmbdBZBBHxT7xGFPvmRBowJpKuDUGYsmu2PLTRwCQmnpUx',
      maxSupply: 6000,
      royalty: {
        ownerAddress: '0x788316ADCBBa96271b9EDEF6730d2E2B43309bc0',
        ownerContact: 'https://t.me/ShibariumPups',
        royaltyAddress: '0x788316ADCBBa96271b9EDEF6730d2E2B43309bc0',
        percentTotal: 500,
      },
      tokenAddress: '0x165E8b1FaEFf503963f113dc81aFc49CeA2bAbA9',
      mintLink: 'https://app.shibariumpups.com/nft-staking/shups-for-wbone',
    },
    {
      name: 'Shibbits',
      slug: 'shibbits',
      symbol: 'Shibbits',
      address: '0xe939c2Cd8160B3a7a9240113B9619FaC995E6bfa',
      description:
        'Shibarium Rabbits are the coolest Rabbits on the scene. With a gear towards titans of industry and high fashion, these Rabbits boost the Liquidity of the $SHUPS token and will be stakeable for $Leash. The collection is few. Catch them if you can.',
      chainId: shibarium.id,
      maxSupply: 216,
      socials: ['https://t.me/Shibariumpups', 'https://x.com/ShibariumPups'],
      website: 'https://www.shibariumpups.com/nft-ecosystem',
      banner:
        'https://api.chewy.build/assets/25fc9e57-d21f-4d15-81e0-10c60c0d6865',
      thumbnail:
        'https://api.chewy.build/assets/3aedab7f-31e1-44da-a7cb-984194c11548',
      metadataSource: 'ipfs://QmYrwWHA7US85uBeeqiXfdkEijqgEdSN4cj3uzPW8Vg7w4/',
      royalty: {
        ownerAddress: '0x788316ADCBBa96271b9EDEF6730d2E2B43309bc0',
        ownerContact: 'https://t.me/ShibariumPups',
        royaltyAddress: '0x788316ADCBBa96271b9EDEF6730d2E2B43309bc0',
        percentTotal: 500,
      },
      tokenAddress: '0x165E8b1FaEFf503963f113dc81aFc49CeA2bAbA9',
      mintLink: 'https://app.shibariumpups.com/mint/shibbits',
    },
    {
      name: 'ShadowKats',
      slug: 'shadowkats',
      symbol: 'ShadowKats',
      address: '0xD69C837f8e6b6E7f829Da64Bd5b7629C4870CE81',
      description:
        'ShadowKats are here to FUD your bag and gain your entry on $TREAT.  They might look cute and even pretend to be your friend, but in reality they are whispering dirty little things about you and even posting passive aggressive things on X! They are the naughty side of the $SHUPS ecosystem and are stakeable for $TREAT. This is the next to last collection in the Shups Ecosystem.',
      chainId: shibarium.id,
      maxSupply: 1620,
      socials: ['https://t.me/Shibariumpups', 'https://x.com/ShibariumPups'],
      website: 'https://www.shibariumpups.com/nft-ecosystem',
      banner:
        'https://api.chewy.build/assets/25fc9e57-d21f-4d15-81e0-10c60c0d6865',
      thumbnail:
        'https://api.chewy.build/assets/f464930e-3560-465b-802a-6c4e5d016e3a',
      metadataSource: 'ipfs://QmRs7xHRxZ2ERSBnMJd2JWBMqAV6g9cbeRCRncbtWWf53i/',
      royalty: {
        ownerAddress: '0x788316ADCBBa96271b9EDEF6730d2E2B43309bc0',
        ownerContact: 'https://t.me/ShibariumPups',
        royaltyAddress: '0x788316ADCBBa96271b9EDEF6730d2E2B43309bc0',
        percentTotal: 500,
      },
      tokenAddress: '0x165E8b1FaEFf503963f113dc81aFc49CeA2bAbA9',
      mintLink: 'https://app.shibariumpups.com/mint/shadowkats',
    },
    {
      name: 'Degenerate Carnie Alliance',
      slug: 'degenerate-carnie-alliance',
      address: '0x34783bb7c14fae7034e8b1e720101ec3dd12c051',
      description: `In the whimsical world of Mr. Radballs Circus, the star attraction isn't just the potent weed or the beer-soaked fun—it's the bizarre and lovable crew of carnies that keep the show running. These aren't your average circus performers; these minions of mischief have gone delightfully off the rails. Each carnie is a mix of simple mindedness and hapless charm, with a penchant for getting into an abundance of trouble, usually under the influence of electric lettuce and beer. Their antics can be as unpredictable as their inebriated thoughts. While no one really knows where these peculiar beings come from, but the legend in Mr. Radballs Circus is that whenever a rug victim cries out, "FUCK!" in frustration, a new carnie is born. It's as if the universe, in its twisted sense of humor, decided to channel the pain of NFT scams into these perpetually cheerful, bumbling creatures.`,
      chainId: shibarium.id,
      symbol: 'DCA',
      banner:
        'https://api.chewy.build/assets/b382ced2-c740-4792-9e5d-439d80b16b9c',
      thumbnail: 'https://api.chewy.build/assets/50960f5e-f05f-4220-ad6c-5a980febf69f',
      website: 'https://radballs.online',
      socials: ['https://t.me/RadballsCircus', 'https://x.com/RadballsCircus'],
      royalty: {
        ownerAddress: '0x10df6CF1A8E99629729179a6d838fD2a8D63D7ed',
        ownerContact: 'https://t.me/MrRadballs',
        royaltyAddress: '0x10df6CF1A8E99629729179a6d838fD2a8D63D7ed',
        percentTotal: 500,
        breakdown: [
          {
            address: '0x10df6CF1A8E99629729179a6d838fD2a8D63D7ed',
            percent: 250,
            description: 'Carnie Beer Fund'
          },
          {
            address: '0x10df6CF1A8E99629729179a6d838fD2a8D63D7ed',
            percent: 250,
            description: 'Spartachris Weed Fund'
          }
        ]
      },
      maxSupply: 10000,
      mintLink: 'https://radballs.online/mint',
    },
    {
      name: 'Dork Wadz',
      slug: 'dorkwadz',
      address: '0x5d74d0e0dd32037512574651f932213b0631d578',
      description: `Bonewhale DorkWadz are a collection of 6969 NFTs on Shibarium inspired by the illustrious BONE token!`,
      chainId: shibarium.id,
      maxSupply: 6969,
      symbol: 'DWADZ',
      banner:
        'https://api.chewy.build/assets/b11834fd-73dd-4e89-a1e0-d75d84303129',
      thumbnail: 'https://api.chewy.build/assets/5952c972-46d3-43eb-a278-a1b5e1055447',
      website: 'https://bonewhale.com',
      socials: [
        'https://twitter.com/bonewhaletoken',
        'https://t.me/bonewhaleportal'
      ],
      royalty: {
        ownerAddress: '0x9EdD11D740C5EFa86bcB8e74fce474d975D93193',
        ownerContact: 'https://t.me/smashSketchum',
        royaltyAddress: '0x9EdD11D740C5EFa86bcB8e74fce474d975D93193',
        percentTotal: 500,
      },
    },
    {
      name: 'DorkWadz DPIX',
      slug: 'dorkwadz-dpix',
      address: '0x2ad1873b3a37fdae87338b6ae37c7ef82bb1629b',
      description: `DorkWadz DPIX is a collection of Pixel art NFTs inspired by the illustrious $BONE token!`,
      chainId: shibarium.id,
      symbol: 'DPIX',
      banner:
        'https://api.chewy.build/assets/638501e5-82c2-4c32-9e0c-b871f992ed05',
      thumbnail: 'https://api.chewy.build/assets/01e714f4-388a-42da-bc1e-02164cfc9f84',
      website: 'https://bonewhale.com',
      socials: [
        'https://twitter.com/bonewhaletoken',
        'https://t.me/bonewhaleportal'
      ],
      royalty: {
        ownerAddress: '0x9EdD11D740C5EFa86bcB8e74fce474d975D93193',
        ownerContact: 'https://t.me/smashSketchum',
        royaltyAddress: '0x9EdD11D740C5EFa86bcB8e74fce474d975D93193',
        percentTotal: 500,
      },
      maxSupply: 10000,
    },
    {
      name: 'The Shib Magazine Covers',
      slug: 'shib-magazine-covers',
      address: '0x007bbf85988caf18cf4222c9214e4fa019b3e002',
      description: `The official The Shib Magazine Cover NFTs in Shibarium. Mint yours for free at news.shib.io`,
      chainId: shibarium.id,
      banner:
        'https://api.chewy.build/assets/1be75499-200b-4b84-9718-9e443c20523b',
      thumbnail: 'https://api.chewy.build/assets/236d6c8c-8bb1-4fdf-b041-c3476d2f4663',
      symbol: 'TheShib',
      website: 'https://news.shib.io/',
      socials: [
        'https://x.com/TheShibmagazine',
        'https://t.me/ShibariumTechnologies'
      ],
      royalty: {
        ownerAddress: '0x28521a4515297A968cE27788Bf12C1E4043513a5',
        ownerContact: 'https://t.me/shiboshisworld',
        royaltyAddress: '0x28521a4515297A968cE27788Bf12C1E4043513a5',
        percentTotal: 500,
        breakdown: [
          {
            address: '0x28521a4515297A968cE27788Bf12C1E4043513a5',
            percent: 500,
            description: 'SHIB Burn'
          }
        ]
      },
      mintLink: 'https://magazine.shib.io/',
    },
    {
      name: 'SHIBOSHIS',
      slug: 'shiboshis',
      address: '0x0c7245d5aaf71aa19b310ad65599b8e36501185c',
      description: `SHIBOSHIS in SHIBARIUM !!! SHIBOSHIS are 10,000 lovable Shiba Inu generated Non Fungible Tokens (NFTS) eternally written on Ethereum and now bridgeable to Shibarium. These Shibs have varying traits that make each unique and collectible (for instance Laser Eyes or Party Hat). Furthermore, each trait corresponds to an enhancement of the base ShibaStrength of the Shiboshi in our upcoming Shiboshi Game.`,
      chainId: shibarium.id,
      socials: ['https://t.me/shiboshisworld', 'https://x.com/shiboshisworld'],
      banner:
        'https://api.chewy.build/assets/d2f76a33-7d25-42fb-ac96-027cf1632319',
      symbol: 'SBSH',
      firstId: 0,
      maxSupply: 10000,
      metadataSource:
        'ipfs://QmUEiYGcZJWZWp9LNCTL5PGhGcjGvokKfcaCoj23dbp79J',
      metadataExt: false,
      noEnumerable: true,
      royalty: {
        ownerAddress: '0x28521a4515297A968cE27788Bf12C1E4043513a5',
        ownerContact: 'https://t.me/shiboshisworld',
        royaltyAddress: '0x28521a4515297A968cE27788Bf12C1E4043513a5',
        percentTotal: 500,
        breakdown: [
          {
            address: '0x28521a4515297A968cE27788Bf12C1E4043513a5',
            percent: 500,
            description: 'SHIB Burn'
          }
        ]
      },
    },
    {
      name: 'Teh Toros',
      slug: 'teh-toros',
      address: '0xb7309486a11982380eeeca2789a157dc4fbaa206',
      description: `Teh Toros is the official 10,000 items collection by Mantra Protocol in Shibarium, showcasing bulls on a journey to enlightenment on the chain. Holding Teh Toros allows you to participate as a share-holder in Mantra Protocol City, but the main goal is the *Illumination of Shibarium*`,
      chainId: shibarium.id,
      maxSupply: 10000,
      symbol: 'Teh Toros',
      banner:
        'https://api.chewy.build/assets/38c9531b-7e24-423b-a378-9fb8e0265ea3',
      thumbnail: 'https://api.chewy.build/assets/e10dce13-a212-498f-9320-329b06dc2f87',
      website: 'https://app.withmantra.com/collections/teh-toros',
      socials: [
        'https://x.com/MantraProtocol',
        'https://t.me/MantraProtocol'
      ],
      metadataSource: 'https://assets-mantra.nyc3.digitaloceanspaces.com/shibarium/code-undefined/metadata/',
    },
    {
      name: 'OG CORRUPTIES',
      slug: 'og-corrupties',
      symbol: 'v 69 CORRUPTIES',
      address: '0x558BA57f773B0B90565C348fAAa6ba86fDD96F7A',
      description:
        'The only ERC721 NFTs that Corrupt Fun will ever release. A set of 100 Corrupties that signifies and benefits each holder like a great value American Express Black Card.',
      chainId: shibarium.id,
      maxSupply: 100,
      socials: ['https://t.me/corruptfun', 'https://x.com/corruptfun', 'https://www.tiktok.com/@corruptfun', 'https://www.reddit.com/r/CorruptFun', 'https://discord.com/invite/TJEmTGdekV'],
      website: 'https://corruptfun.com',
      banner:
        'https://api.chewy.build/assets/7b828f43-1d6d-4641-9c62-1eb59c4ece25',
      thumbnail: 'https://api.chewy.build/assets/1f99ccb5-6986-4115-a274-9cf679f3f053',
      metadataSource: 'ipfs://QmdbY8pcb6cr54u2EakTwHcueDTmP92yC15NjkG8ctFMgM/',
      royalty: {
        ownerAddress: '0x72e4F6724B45AcC526D486b5D7d7213C54F0bd9A',
        ownerContact: 'https://t.me/derbro',
        royaltyAddress: '0x72e4F6724B45AcC526D486b5D7d7213C54F0bd9A',
        percentTotal: 500,
      },
      tokenAddress: '0x8cC82045E761329FA13C9b0A0a31d76615fEc109',
    },
    {
      name: 'SleepyShibs',
      slug: 'sleepyshibs',
      address: '0xeDc49691620fa3Dab9aA7577f1d089c1Db6D8444',
      description:
        "A collection of 3333 hand-drawn, randomly generated NFT's that have found their forever home on Sleepy Street, Shibarium!",
      chainId: shibarium.id,
      symbol: 'SS',
      collectionType: 'ERC721',
      maxSupply: 3333,
      mintActive: false,
      website: 'https://www.sleepyshibs.com ',
      socials: ['https://t.me/Sleepyshib', 'https://x.com/_SleepyShibs_'],
      banner:
        'https://api.chewy.build/assets/3812b22c-c165-46e0-a407-16b303f3052d',
      thumbnail:
        'https://api.chewy.build/assets/41cd0351-10bd-4c19-b1c7-eafd176d5a7e',
      royalty: {
        ownerAddress: '0xBFE127779f4b06AEB5B5160e048B0B83Df636Ab4',
        ownerContact: 'https://t.me/ShibHodler91 ',
        royaltyAddress: '0xBFE127779f4b06AEB5B5160e048B0B83Df636Ab4',
        percentTotal: 700,
      },
      tokenAddress: '0x8cC82045E761329FA13C9b0A0a31d76615fEc109',
    },
    {
      name: 'Shibarium Ape Yacht Club',
      slug: 'sayc',
      symbol: 'SAYC',
      address: '0x7137169EF6ae19a3eA180C86796B923764CA821d',
      description: `Shiba Ape Yacht Club - 10k apes on Shibarium. A Community Takeover Project, the original dev rugged mint funds and never made good on promises so the community has taken control over the project as is the spirit of Shibarium!`,
      chainId: shibarium.id,
      maxSupply: 10000,
      banner:
        'https://api.chewy.build/assets/97e755c3-6baa-4553-98d2-cfc3dd533839',
      thumbnail:
        'https://api.chewy.build/assets/2370ab52-e4a6-40cc-b930-4c512c27d98d',
      socials: ['https://x.com/sayc_nft', 'https://t.me/+cMk7zY5Ax50zNGIx'],
      website: 'https://saycshibarium.github.io/sayc',
      royalty: {
        ownerAddress: '0xf6b1864e480c011cE9C066d4CF88B92513dd4ae0',
        ownerContact: 'https://t.me/ShibHodler91 ',
        royaltyAddress: '0xf6b1864e480c011cE9C066d4CF88B92513dd4ae0',
        percentTotal: 500,
      },
      metadataSource: `${defaultIpfsGateway}bafybeicvawyofk44knagwmdwrdkvu74akjx5nb6qvivu3tkcrfoftx6ody/`,
    },
    {
      name: 'Salute NFT',
      slug: 'salute',
      symbol: 'SALUTE',
      address: '0x19eB60cB3A3f489E3e45988feCBAB2FA73535620',
      description: `100 Legendary Salutes. One Shib Army. Infinite Loyalty. Step into meme history with the Salute NFT Collection a limited set of 100 character-driven NFTs paying tribute to the unstoppable spirit of the Shiba Inu community. Each NFT represents a proud member of the $SALUTE battalion, standing tall to salute the rise of $BONE and $LEASH to their moon-shot destinies. Not just collectibles — these NFTs work for the mission. Stake Salute NFTs to earn $SALUTE tokens and strengthen the ecosystem with every salute.`,
      chainId: shibarium.id,
      maxSupply: 100,
      website: 'https://www.newshibariumplays.com/shibarium-projects/salute',
      socials: ['https://t.me/salutebone'],
      banner:
        'https://api.chewy.build/assets/60e619c2-8f40-40b9-bb66-9be26f9fa3e3',
      thumbnail:
        'https://api.chewy.build/assets/02b0e292-83e9-485f-99f5-fa9f2514bf59',
      royalty: {
        ownerAddress: '0xB6F704AAfA66D2835F1cf91df65A1797fc432f0c',
        ownerContact: 'https://t.me/RealBossman',
        royaltyAddress: '0xB6F704AAfA66D2835F1cf91df65A1797fc432f0c',
        percentTotal: 500,
      },
      tokenAddress: '0x1d57ed76a064733cbb0263b9f877b146755437fb',
    },
    {
      name: '$69 DAO FLIGHT PASSES',
      slug: '69-dao-flight-passes',
      symbol: '69DAOPASS',
      address: '0x294B1F0b9b3873122Bf57Fea159C8898a2B0f555',
      description: `Owning a $69 DAO FLIGHT PASS will grant holders access to the private $69 on Shibarium DAO Group + Benefits such as NFT Staking & More.`,
      chainId: shibarium.id,
      maxSupply: 69,
      website: 'https://www.newshibariumplays.com/69 ',
      socials: ['http://t.me/Shibarium69', 'https://x.com/69onshibarium'],
      banner:
        'https://api.chewy.build/assets/3ca36ad0-5626-4adb-9ea5-215c408cfb31',
      thumbnail:
        'https://api.chewy.build/assets/aee9bc78-0ef7-4a72-96f8-55e955a07a1f',
      royalty: {
        ownerAddress: '0x45c2536dfE9f73395519e1F10f2e5f392Aa3558A',
        ownerContact: 'https://t.me/RealBossman',
        royaltyAddress: '0x45c2536dfE9f73395519e1F10f2e5f392Aa3558A',
        percentTotal: 500,
      },
      tokenAddress: '0x4b46bae20730916ceef88201fac2945542a11a09',
      noEnumerable: true,
      isGif: true,
      metadataSource: `${defaultIpfsGateway}QmTDykQXc4fMayPg2imWASyDRj9gU8uobbmbg78Tn8u68V/`,
      metadataExt: false
    },
    {
      name: 'One Cent Dream Origins',
      slug: 'one-cent-dream-origins',
      symbol: 'CENT',
      address: '0x7a572c41B4Da4d3805e2008C0C3Cf90BbA797357',
      description: `The One Cent Dream Origins comprise a curated collection of 94 unique NFTs that were meticulously crafted by the community throughout the evolution of One Cent Dream.`,
      chainId: shibarium.id,
      maxSupply: 94,
      banner: 'https://api.chewy.build/assets/eefdadfd-ebf8-4ffe-87fc-7ecf64d8b82c',
      socials: ['https://x.com/1cDream', 'https://t.me/CENT_1CENTDREAM'],
      website: 'https://fef.solutions/1-cent-dream-cent',
      royalty: {
        ownerAddress: '0x523Cef3aF353c0A98332d0dfCD5d29280F2f664b',
        ownerContact: 'https://t.me/mikeewx',
        royaltyAddress: '0x523Cef3aF353c0A98332d0dfCD5d29280F2f664b',
        percentTotal: 500,
      },
      tokenAddress: '0x3C9b2b930ed1B6040F8539CCeB35Dd189F4733dD',
      noEnumerable: true,
    },
    {
      name: 'FlexibleNFT',
      slug: 'flexible-nft',
      symbol: 'FNFT',
      address: '0x6f33eA4B928177C6D7051355570d2b2cf93b24d6',
      description: `FEED community has original NFT pictures.
The FEED JAPAN community provides its own LP (liquidity pool) and gives away NFT images every month as a thank you for providing FEED.
By collecting them, you can complete a four-character idiom, so some people may want to collect them.
Since the largest number of FEED holders are from the Chinese community, we used Chinese characters in the bottom right so that everyone can understand.
There is a chance of winning three premium star version cards and one premium rainbow version card at random every month.`,
      chainId: shibarium.id,
      banner: 'https://api.chewy.build/assets/6dfb9186-25c0-4e1b-9b69-b4e736c771c7',
      thumbnail: 'https://api.chewy.build/assets/66b5374b-fb1b-4bed-bfa4-1e79587f7faa',
      socials: ['https://t.me/feedcto/1'],
      website: 'https://feed.shibarium.fun/',
      royalty: {
        ownerContact: 'https://t.me/EdSheeeran_SHIB',
        royaltyAddress: '0x523Cef3aF353c0A98332d0dfCD5d29280F2f664b',
        percentTotal: 500,
      },
      noEnumerable: true,
      maxSupply: 300,
    },
    /*
    {
      name: 'Serge',
      slug: 'serge',
      symbol: 'S',
      address: '0x9FcEa3627746aEA84c8Eba612E1fD25aC27d605A',
      description: ``,
      chainId: shibarium.id,
      banner: 'https://api.chewy.build/assets/6dfb9186-25c0-4e1b-9b69-b4e736c771c7',
      thumbnail: 'https://api.chewy.build/assets/66b5374b-fb1b-4bed-bfa4-1e79587f7faa',
      socials: ['https://t.me/feedcto/1'],
      website: 'https://feed.shibarium.fun/',
      royalty: {
        ownerContact: 'https://t.me/EdSheeeran_SHIB',
        royaltyAddress: '0x523Cef3aF353c0A98332d0dfCD5d29280F2f664b',
        percentTotal: 500,
      },
      noEnumerable: true,
      maxSupply: 300,
    }, */
  ]
  // Add more chains as needed
}

/**
 * Get all supported NFT collections for a specific chain
 */
export function getMarketplaceCollections(chainId: ChainType): NFTCollection[] {
  return COLLECTIONS_BY_CHAIN[chainId] || []
}

/**
 * Get a specific collection by its address and chain ID
 */
export function getCollectionByAddress(
  address: string,
  chainId: ChainType
): NFTCollection | undefined {
  if (!address) {
    return undefined
  }
  const collections = COLLECTIONS_BY_CHAIN[chainId] || []
  return collections.find(
    collection => collection.address && collection.address.toLowerCase() === address.toLowerCase()
  )
}

/**
 * Get all supported chains that have collections
 */
export function getSupportedCollectionChains(): number[] {
  return Object.keys(COLLECTIONS_BY_CHAIN).map(id => Number(id))
}

/**
 * Get a collection by its slug and chain ID
 */
export function getCollectionBySlug(
  slug: string,
  chainId: ChainType
): NFTCollection | undefined {
  if (!slug) {
    return undefined
  }
  const collections = COLLECTIONS_BY_CHAIN[chainId] || []
  return collections.find(
    collection => collection.slug === slug
  )
}

/**
 * Get a collection by its name and chain ID
 */
export function getCollectionByName(
  name: string,
  chainId: ChainType
): NFTCollection | undefined {
  if (!name) {
    return undefined
  }
  const collections = COLLECTIONS_BY_CHAIN[chainId] || []
  return collections.find(
    collection => collection.name.toLowerCase() === name.toLowerCase()
  )
}

/**
 * Generate a URL-friendly slug from a collection name
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim()
}

/**
 * Validate that all collections have unique slugs within their chain
 */
export function validateCollectionSlugs(): { isValid: boolean; errors: string[] } {
  const errors: string[] = []

  for (const [chainId, collections] of Object.entries(COLLECTIONS_BY_CHAIN)) {
    const slugs = new Set<string>()

    for (const collection of collections) {
      if (!collection.slug) {
        errors.push(`Collection "${collection.name}" on chain ${chainId} is missing a slug`)
        continue
      }

      if (slugs.has(collection.slug)) {
        errors.push(`Duplicate slug "${collection.slug}" found on chain ${chainId}`)
      }

      slugs.add(collection.slug)
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Check if a collection address is whitelisted for marketplace functionality
 */
export function isCollectionWhitelisted(
  address: string,
  chainId: ChainType
): boolean {
  if (!address) return false;
  return !!getCollectionByAddress(address, chainId);
}
